import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { personnel, projects } from '../../data/sampleData';
import { SearchIcon, FilterIcon, MoreHorizontalIcon, UserPlusIcon, XIcon, FileTextIcon, PaperclipIcon } from 'lucide-react';
type TabType = 'personnel' | 'projects';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-202">
      <div className="absolute inset-0 bg-gray-12 opacity-40" onClick={onClose} data-id="element-203"></div>
      <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 z-10" data-id="element-204">
        <div className="flex justify-between items-center p-6 border-b border-gray-6" data-id="element-205">
          <h2 className="text-lg font-semibold text-gray-12" data-id="element-206">{title}</h2>
          <button onClick={onClose} className="text-gray-11 hover:text-gray-12" data-id="element-207">
            <XIcon size={20} data-id="element-208" />
          </button>
        </div>
        <div className="p-6" data-id="element-209">{children}</div>
      </div>
    </div>;
};
export const DatabasePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('personnel');
  const [isModalOpen, setIsModalOpen] = useState(false);
  return <div data-id="element-210">
      <div className="flex justify-between items-center mb-8" data-id="element-211">
        <h1 className="text-2xl font-bold text-gray-12" data-id="element-212">Database</h1>
        {activeTab === 'personnel' && <Button icon={<UserPlusIcon size={16} data-id="element-214" />} onClick={() => setIsModalOpen(true)} data-id="element-213">
            Add Personnel
          </Button>}
        {activeTab === 'projects' && <Button icon={<UserPlusIcon size={16} data-id="element-216" />} data-id="element-215">Add Project</Button>}
      </div>
      <div className="mb-6" data-id="element-217">
        <div className="border-b border-gray-6" data-id="element-218">
          <nav className="-mb-px flex space-x-8" data-id="element-219">
            <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'personnel' ? 'border-indigo-9 text-indigo-9' : 'border-transparent text-gray-11 hover:text-gray-12 hover:border-gray-6'}`} onClick={() => setActiveTab('personnel')} data-id="element-220">
              Personnel
            </button>
            <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'projects' ? 'border-indigo-9 text-indigo-9' : 'border-transparent text-gray-11 hover:text-gray-12 hover:border-gray-6'}`} onClick={() => setActiveTab('projects')} data-id="element-221">
              Projects
            </button>
          </nav>
        </div>
      </div>
      <div className="flex items-center space-x-4 mb-6" data-id="element-222">
        <div className="relative" data-id="element-223">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-224">
            <SearchIcon size={16} className="text-gray-10" data-id="element-225" />
          </div>
          <input type="text" placeholder={activeTab === 'personnel' ? 'Search by name...' : 'Search projects...'} className="pl-10 pr-4 py-2.5 h-10 border border-gray-7 rounded-md text-sm focus:ring-2 focus:ring-indigo-9 focus:border-transparent w-64" data-id="element-226" />
        </div>
        <Button variant="secondary" size="sm" icon={<FilterIcon size={14} data-id="element-228" />} data-id="element-227">
          {activeTab === 'personnel' ? 'Department' : 'Location'}
        </Button>
        <Button variant="secondary" size="sm" data-id="element-229">
          {activeTab === 'personnel' ? 'Location' : 'Client'}
        </Button>
      </div>
      {activeTab === 'personnel' && <div className="bg-white border border-gray-6 rounded-lg overflow-hidden" data-id="element-230">
          <table className="min-w-full divide-y divide-gray-6" data-id="element-231">
            <thead className="bg-gray-2" data-id="element-232">
              <tr data-id="element-233">
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-234">
                  Name
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-235">
                  Title
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-236">
                  Email
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-237">
                  Phone
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-238">
                  Resume
                </th>
                <th scope="col" className="relative px-4 py-3" data-id="element-239">
                  <span className="sr-only" data-id="element-240">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-6" data-id="element-241">
              {personnel.map((person, idx) => <tr key={person.id} className={`hover:bg-gray-2 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-1'}`} data-id="element-242">
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-243">
                    <div className="flex items-center" data-id="element-244">
                      <div className="h-8 w-8 bg-gray-4 rounded-full flex items-center justify-center mr-3" data-id="element-245">
                        <span className="font-medium text-gray-11" data-id="element-246">
                          {person.avatar}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-12" data-id="element-247">
                        {person.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-248">
                    <div className="text-sm text-gray-11" data-id="element-249">{person.title}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-250">
                    <div className="text-sm text-gray-11" data-id="element-251">{person.email}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-252">
                    <div className="text-sm text-gray-11" data-id="element-253">{person.phone}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-254">
                    {person.hasResume ? <div className="text-indigo-9" data-id="element-255">
                        <FileTextIcon size={16} data-id="element-256" />
                      </div> : <div className="text-gray-8" data-id="element-257">-</div>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-258">
                    <button className="text-gray-11 hover:text-gray-12" data-id="element-259">
                      <MoreHorizontalIcon size={16} data-id="element-260" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
      {activeTab === 'projects' && <div className="bg-white border border-gray-6 rounded-lg overflow-hidden" data-id="element-261">
          <table className="min-w-full divide-y divide-gray-6" data-id="element-262">
            <thead className="bg-gray-2" data-id="element-263">
              <tr data-id="element-264">
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-265">
                  Project Name
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-266">
                  Client
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-267">
                  Location
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-268">
                  Completed
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-269">
                  Value
                </th>
                <th scope="col" className="relative px-4 py-3" data-id="element-270">
                  <span className="sr-only" data-id="element-271">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-6" data-id="element-272">
              {projects.map((project, idx) => <tr key={project.id} className={`hover:bg-gray-2 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-1'}`} data-id="element-273">
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-274">
                    <div className="text-sm font-medium text-gray-12" data-id="element-275">
                      {project.name}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-276">
                    <div className="text-sm text-gray-11" data-id="element-277">{project.client}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-278">
                    <div className="text-sm text-gray-11" data-id="element-279">
                      {project.location}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-280">
                    <div className="text-sm text-gray-11" data-id="element-281">
                      {project.completedDate}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-282">
                    <div className="text-sm text-gray-11" data-id="element-283">{project.value}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-284">
                    <button className="text-gray-11 hover:text-gray-12" data-id="element-285">
                      <MoreHorizontalIcon size={16} data-id="element-286" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Add Personnel" data-id="element-287">
        <form className="space-y-4" data-id="element-288">
          <div data-id="element-289">
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-11 mb-1" data-id="element-290">
              Full Name *
            </label>
            <input type="text" id="fullName" className="w-full px-3 py-2.5 border border-gray-7 rounded-md text-sm focus:ring-2 focus:ring-indigo-9 focus:border-transparent" data-id="element-291" />
          </div>
          <div data-id="element-292">
            <label htmlFor="title" className="block text-sm font-medium text-gray-11 mb-1" data-id="element-293">
              Title
            </label>
            <input type="text" id="title" className="w-full px-3 py-2.5 border border-gray-7 rounded-md text-sm focus:ring-2 focus:ring-indigo-9 focus:border-transparent" data-id="element-294" />
          </div>
          <div data-id="element-295">
            <label htmlFor="email" className="block text-sm font-medium text-gray-11 mb-1" data-id="element-296">
              Email *
            </label>
            <input type="email" id="email" className="w-full px-3 py-2.5 border border-gray-7 rounded-md text-sm focus:ring-2 focus:ring-indigo-9 focus:border-transparent" data-id="element-297" />
          </div>
          <div data-id="element-298">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-11 mb-1" data-id="element-299">
              Phone
            </label>
            <input type="tel" id="phone" className="w-full px-3 py-2.5 border border-gray-7 rounded-md text-sm focus:ring-2 focus:ring-indigo-9 focus:border-transparent" data-id="element-300" />
          </div>
          <div data-id="element-301">
            <label htmlFor="resume" className="block text-sm font-medium text-gray-11 mb-1" data-id="element-302">
              Resume (PDF)
            </label>
            <div className="flex items-center mt-1" data-id="element-303">
              <label htmlFor="file-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-7 rounded-md text-sm font-medium text-gray-11 hover:bg-gray-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-9 flex items-center" data-id="element-304">
                <PaperclipIcon size={16} className="mr-1" data-id="element-305" /> Choose File
                <input id="file-upload" name="file-upload" type="file" className="sr-only" data-id="element-306" />
              </label>
              <span className="ml-3 text-sm text-gray-10" data-id="element-307">No file chosen</span>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-gray-6" data-id="element-308">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-309">
              Cancel
            </Button>
            <Button data-id="element-310">Save Personnel</Button>
          </div>
        </form>
      </Modal>
    </div>;
};